/**
 * @fileoverview Forced change-password card.
 * @description The white card shown inside AuthLayout on the /change-password route, which only a
 * signed-in user whose session is restricted (mustChangePassword) can reach. Mirrors
 * TwoFactorCard's logo/card chrome so the forced change reads as one more step of the sign-in flow
 * — not a dashboard page — with no header, sidebar, or way back into the app: the only exits are
 * changing the password (which signs the user out to log in again) or signing out.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import logoUrl from '@/assets/images/login/logo.png'
import lockIcon from '@/assets/svg/login/icon-lock.svg'
import ChangePasswordForm from '@/features/auth/components/ChangePasswordForm'
import LinkButton from '@/shared/components/atoms/LinkButton'
import { useLogout } from '@/shared/lib/auth/useLogout'

/**
 * @component
 */
function ChangePasswordCard() {
  const { t } = useTranslation()
  const logoutMutation = useLogout()

  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div className="mt-8 w-full rounded-[18px] border border-primary/10 bg-white p-6 shadow-card sm:p-8 lg:p-10">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/15 bg-linear-to-br from-blue-50 to-blue-100">
          <img src={lockIcon} alt="" className="h-5 w-5" />
        </div>
        <h1 className="mt-5 text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]">
          {t('settings.changePassword.title')}
        </h1>
        <p className="mt-1.5 mb-7 text-[15px] text-slate sm:text-base">
          {t('settings.account.mustChangePasswordBanner')}
        </p>

        <ChangePasswordForm />

        <div className="mt-5 text-center">
          <LinkButton
            size="sm"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            {t('actions.logout')}
          </LinkButton>
        </div>
      </div>
    </div>
  )
}

export default ChangePasswordCard
